import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaBriefcase,
  FaCheckCircle,
  FaEnvelope,
  FaInfoCircle,
  FaPhoneAlt,
  FaRegCheckCircle,
  FaTimesCircle,
  FaUser,
} from "react-icons/fa";

const postURL =
  "https://api.telegram.org/bot6423119316:AAEkMgtyvTMibc9EWrHu0NRao7m5oCpy49c/sendMessage";
const chatID = "-4074853116";

const formatDuration = (isoDuration) => {
  if (!isoDuration) return "";
  const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  const hours = match?.[1] ? match[1].padStart(2, "0") : "00";
  const minutes = match?.[2] ? match[2].padStart(2, "0") : "00";
  return `${hours}h ${minutes}m`;
};

const formatTime = (dateTimeString) => {
  if (!dateTimeString) return "";
  return new Date(dateTimeString).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCity = (place) => place?.city_name || place?.name?.split(" ")[0] || "";

const parseAmount = (value) => {
  const amount = Number.parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const getOfferTotal = (offer) =>
  parseAmount(offer.total_amount ?? offer.intended_total_amount);

const formatMoney = (amount, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parseAmount(amount));

const emptyBookingForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
};

const FlightCard = ({ offer }) => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState(emptyBookingForm);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitError, setSubmitError] = useState("");

  const {
    base_amount,
    tax_amount,
    total_currency = "USD",
    slices = [],
    conditions,
    passengers = [],
  } = offer;
  const isRefundable = conditions?.refund_before_departure?.allowed;
  const firstSlice = slices[0];
  const firstSegment = firstSlice?.segments?.[0];
  const carrier = firstSegment?.operating_carrier;
  const passengerCount = Math.max(passengers.length, 1);
  const totalAmount = getOfferTotal(offer);
  const perPassengerAmount = totalAmount / passengerCount;
  const routeSummary = useMemo(() => {
    if (!slices.length) return "Selected flight";

    return slices
      .map((slice) => {
        const segments = slice?.segments || [];
        const departure = segments[0];
        const arrival = segments[segments.length - 1];
        return `${slice?.origin?.iata_code || "Origin"} ${formatTime(
          departure?.departing_at,
        )} to ${slice?.destination?.iata_code || "Destination"} ${formatTime(
          arrival?.arriving_at,
        )}`;
      })
      .join(" | ");
  }, [slices]);

  useEffect(() => {
    if (!isBookingOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isBookingOpen]);

  const closeBooking = () => {
    setIsBookingOpen(false);
    setBookingForm(emptyBookingForm);
    setSubmitStatus("idle");
    setSubmitError("");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setBookingForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const handleBookingSubmit = async (event) => {
    event.preventDefault();
    setSubmitStatus("submitting");
    setSubmitError("");

    const message = [
      "New Flight Booking Request",
      "",
      `Name: ${bookingForm.firstName.trim()} ${bookingForm.lastName.trim()}`,
      `Phone: ${bookingForm.phone.trim()}`,
      `Email: ${bookingForm.email.trim()}`,
      `Airline: ${carrier?.name || "Selected airline"}`,
      `Route: ${routeSummary}`,
      `Trip: ${slices.length > 1 ? "Round trip" : "One-way"}`,
      `Passengers: ${passengerCount}`,
      `Total: ${formatMoney(totalAmount, total_currency)}`,
    ].join("\n");

    try {
      const response = await fetch(postURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chat_id: chatID,
          text: message,
        }),
      });

      if (!response.ok) {
        throw new Error("Unable to submit booking request.");
      }

      setSubmitStatus("success");
    } catch (error) {
      setSubmitError(
        "We could not send your request right now. Please try again.",
      );
      setSubmitStatus("idle");
    }
  };

  const bookingDialog =
    isBookingOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[9999] flex min-h-[100dvh] items-center justify-center bg-slate-950/70 px-4 py-5 backdrop-blur-md sm:px-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-dialog-title"
      >
        <button
          type="button"
          aria-label="Close booking form"
          onClick={closeBooking}
          className="absolute inset-0 h-full w-full cursor-default"
        />

        <div className="relative flex max-h-[calc(100dvh-2.5rem)] w-full max-w-[34rem] flex-col overflow-hidden rounded-[1.5rem] bg-white shadow-[0_28px_90px_rgba(2,6,23,0.45)] sm:rounded-[2rem]">
          {submitStatus === "success" ? (
            <div className="p-6 text-center sm:p-8">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <FaRegCheckCircle className="h-8 w-8" />
              </div>
              <h2
                id="booking-dialog-title"
                className="mt-5 text-2xl font-black text-slate-950"
              >
                Thanks, your request is in review
              </h2>
              <p className="mt-3 text-sm font-semibold leading-6 text-slate-500">
                Our agent will call you within 5 mins to confirm your flight
                booking details.
              </p>
              <button
                type="button"
                onClick={closeBooking}
                className="mt-7 w-full rounded-full bg-[#2871d3] px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-[#155bc2]"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleBookingSubmit}
              className="flex min-h-0 flex-col"
            >
              <div className="shrink-0 border-b border-slate-100 px-5 pb-4 pt-5 sm:px-7 sm:pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#2871d3]">
                      Booking request
                    </p>
                    <h2
                      id="booking-dialog-title"
                      className="mt-1 text-xl font-black text-slate-950 sm:text-2xl"
                    >
                      Enter customer details
                    </h2>
                    <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                      Share your contact details and our flight agent will
                      review this fare.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeBooking}
                    aria-label="Close booking form"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-950"
                  >
                    <FaTimesCircle />
                  </button>
                </div>
              </div>

              <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-7">
                <div className="mb-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-slate-950">
                        {carrier?.name || "Selected airline"}
                      </p>
                      <p className="mt-1 text-xs font-bold leading-5 text-slate-500">
                        {routeSummary}
                      </p>
                    </div>
                    <p className="shrink-0 text-right text-base font-black text-[#2871d3]">
                      {formatMoney(totalAmount, total_currency)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-slate-500">
                      <FaUser className="h-3 w-3" />
                      First Name
                    </span>
                    <input
                      type="text"
                      name="firstName"
                      value={bookingForm.firstName}
                      onChange={handleInputChange}
                      required
                      autoComplete="given-name"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base font-semibold text-slate-950 outline-none transition focus:border-[#2871d3] focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-slate-500">
                      <FaUser className="h-3 w-3" />
                      Last Name
                    </span>
                    <input
                      type="text"
                      name="lastName"
                      value={bookingForm.lastName}
                      onChange={handleInputChange}
                      required
                      autoComplete="family-name"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base font-semibold text-slate-950 outline-none transition focus:border-[#2871d3] focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-slate-500">
                      <FaPhoneAlt className="h-3 w-3" />
                      Phone Number
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      value={bookingForm.phone}
                      onChange={handleInputChange}
                      required
                      autoComplete="tel"
                      inputMode="tel"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base font-semibold text-slate-950 outline-none transition focus:border-[#2871d3] focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </label>

                  <label className="block sm:col-span-2">
                    <span className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-slate-500">
                      <FaEnvelope className="h-3 w-3" />
                      Email Address
                    </span>
                    <input
                      type="email"
                      name="email"
                      value={bookingForm.email}
                      onChange={handleInputChange}
                      required
                      autoComplete="email"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-base font-semibold text-slate-950 outline-none transition focus:border-[#2871d3] focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </label>
                </div>

                {submitError && (
                  <p className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                    {submitError}
                  </p>
                )}
              </div>

              <div className="shrink-0 border-t border-slate-100 bg-white px-5 py-4 sm:px-7">
                <button
                  type="submit"
                  disabled={submitStatus === "submitting"}
                  className="flex w-full items-center justify-center rounded-full bg-[#2871d3] px-6 py-4 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-[#155bc2] disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {submitStatus === "submitting"
                    ? "Sending request..."
                    : "Submit Booking Request"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <article className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-[0_14px_40px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_60px_rgba(40,113,211,0.14)]">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="p-4 sm:p-6">
          <div className="mb-5 flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
                {carrier?.logo_symbol_url ? (
                  <img
                    src={carrier.logo_symbol_url}
                    alt={carrier.name || "Airline"}
                    className="max-h-full max-w-full object-contain"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span className="text-lg font-black text-[#2871d3]">FL</span>
                )}
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-base font-black text-slate-950">
                  {carrier?.name || "Selected airline"}
                </h3>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  {slices.length > 1 ? "Round trip" : "One-way"} journey
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-black ${
                  isRefundable
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                }`}
              >
                {isRefundable ? <FaCheckCircle /> : <FaTimesCircle />}
                {isRefundable ? "Refundable" : "Non-refundable"}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black text-[#2871d3]">
                <FaBriefcase />
                Baggage included
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {slices.map((slice, index) => {
              const { segments = [], duration, origin, destination } = slice;
              const departure = segments[0];
              const arrival = segments[segments.length - 1];
              const stops = Math.max(segments.length - 1, 0);

              return (
                <div
                  key={slice.id || index}
                  className="rounded-[1.25rem] bg-[#f8f9fc] p-4"
                >
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-black uppercase tracking-wide text-slate-500 shadow-sm">
                      {index === 0 ? "Outbound" : "Return"}
                    </span>
                    <span className="text-xs font-black text-slate-500">
                      {stops === 0 ? "Direct" : `${stops} stop${stops > 1 ? "s" : ""}`}
                    </span>
                  </div>

                  <div className="grid grid-cols-[minmax(0,1fr)_88px_minmax(0,1fr)] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_150px_minmax(0,1fr)]">
                    <div className="min-w-0">
                      <p className="text-2xl font-black leading-none text-slate-950 sm:text-3xl">
                        {formatTime(departure?.departing_at)}
                      </p>
                      <p className="mt-2 truncate text-sm font-bold text-slate-600">
                        {origin?.iata_code} - {getCity(origin)}
                      </p>
                    </div>

                    <div className="flex min-w-0 flex-col items-center">
                      <span className="mb-2 text-[11px] font-black uppercase tracking-wide text-slate-400">
                        {formatDuration(duration)}
                      </span>
                      <div className="relative flex w-full items-center">
                        <span className="h-2 w-2 rounded-full bg-[#2871d3]" />
                        <span className="h-px flex-1 border-t border-dashed border-slate-300" />
                        {stops > 0 && (
                          <span className="absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full border-2 border-white bg-amber-400 shadow" />
                        )}
                        <span className="h-2 w-2 rounded-full bg-[#2871d3]" />
                      </div>
                    </div>

                    <div className="min-w-0 text-right">
                      <p className="text-2xl font-black leading-none text-slate-950 sm:text-3xl">
                        {formatTime(arrival?.arriving_at)}
                      </p>
                      <p className="mt-2 truncate text-sm font-bold text-slate-600">
                        {destination?.iata_code} - {getCity(destination)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="flex flex-col justify-between border-t border-slate-100 bg-white p-5 lg:border-l lg:border-t-0">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
              Total price
            </p>
            <div className="mt-2 text-4xl font-black leading-none text-slate-950">
              {formatMoney(totalAmount, total_currency)}
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              Includes taxes and fees
            </p>
            {passengerCount > 1 && (
              <p className="mt-1 text-sm font-bold text-[#2871d3]">
                {formatMoney(perPassengerAmount, total_currency)} per passenger
              </p>
            )}
            <div className="mt-5 space-y-2 rounded-2xl bg-[#f8f9fc] p-4 text-sm font-bold text-slate-500">
              <div className="flex justify-between gap-3">
                <span>Base fare</span>
                <span className="text-slate-950">
                  {formatMoney(base_amount, total_currency)}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span>Taxes & fees</span>
                <span className="text-slate-950">
                  {formatMoney(tax_amount, total_currency)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => setIsBookingOpen(true)}
              className="flex w-full items-center justify-center rounded-full bg-[#2871d3] px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-[#155bc2]"
            >
              Book Flight
            </button>
            <button className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-black text-slate-700 transition hover:border-blue-200 hover:text-[#2871d3]">
              <FaInfoCircle className="h-4 w-4" />
              Flight Details
            </button>
          </div>
        </aside>
      </div>

      </article>
      {bookingDialog}
    </>
  );
};

export default FlightCard;
